/**
 * Per-programme content for the five Focus Area pages. Everything that differs
 * between them lives here, keyed by the same slug the route uses — objectives,
 * the walkthrough video, and which stories, articles, gallery items and
 * documents that programme surfaces.
 *
 * Indices point into the shared content pools in `resources.ts`, standing in for
 * the CMS relations until the two apps share an API.
 */

import { type DocCollection } from './resources';
import type { ArticleItem, DownloadItem, GalleryItem } from '@components/pages/ResourceSections';

export interface FocusObjective {
    title: string;
    body: string;
}

export interface FocusVideo {
    heading: string;
    copy: string;
    /** Poster frame. */
    thumbnail: string;
    /** Embed URL. Absent until one is attached in the CMS. */
    url?: string;
}

/** What a programme has attached, resolved against the CMS pools. */
export interface FocusAttachments {
    stories: ArticleItem[];
    articles: ArticleItem[];
    gallery: GalleryItem[];
    documents: Record<DocCollection, DownloadItem[]>;
}

export interface FocusContent {
    objectives: FocusObjective[];
    video: FocusVideo;
    /** Set once the CMS has answered; from then on it alone decides what a page shows. */
    attached?: FocusAttachments;
}

const DEMO_VIDEO_IMG = '/focus/demo-video.webp';

/**
 * Programme page content, as the CMS publishes it. The bundled set that stood
 * here attached its content by index into the sample pools; the CMS attaches by
 * reference, resolved into `attached` when the feed lands.
 */
export const FOCUS_CONTENT: Record<string, FocusContent> = {};

/** A programme the CMS added: nothing attached until an editor attaches it. */
export const blankFocusContent = (): FocusContent => ({
    objectives: [],
    video: { heading: '', copy: '', thumbnail: DEMO_VIDEO_IMG },
    attached: {
        stories: [],
        articles: [],
        gallery: [],
        documents: { publications: [], reports: [], infographics: [] },
    },
});

/** Falls back to the Tech For Justice shape so a new slug never renders empty. */
export const getFocusContent = (slug?: string): FocusContent =>
    (slug && FOCUS_CONTENT[slug]) || FOCUS_CONTENT['tech-for-justice'];

/* ── Relation helpers ─────────────────────────────────────────────────── */

export const focusStories = (c: FocusContent): ArticleItem[] => c.attached?.stories ?? [];
export const focusArticles = (c: FocusContent): ArticleItem[] => c.attached?.articles ?? [];
export const focusGallery = (c: FocusContent): GalleryItem[] => c.attached?.gallery ?? [];

/** Documents, per collection, as the CMS attached them. */
export const focusDocuments = (c: FocusContent): Record<DocCollection, DownloadItem[]> =>
    c.attached?.documents ?? { publications: [], reports: [], infographics: [] };
